// src/services/msalService.ts
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../config/authConfig";
import { teamsAuthService } from "./teamsService";

export const msalInstance = new PublicClientApplication(msalConfig);

let isInitialized = false;
let initializationPromise: Promise<void> | null = null;

/**
 * Initialize MSAL instance (only if not in Teams)
 */
export const initializeMsal = async (): Promise<void> => {
    // Check if running in Teams first
    const teamsContext = await teamsAuthService.initialize();

    if (teamsContext.isInTeams) {
        isInitialized = true;
        return Promise.resolve();
    }

    if (isInitialized) {
        return Promise.resolve();
    }

    if (initializationPromise) {
        return initializationPromise;
    }

    initializationPromise = msalInstance
        .initialize()
        .then(() => {
            const accounts = msalInstance.getAllAccounts();

            if (accounts.length > 0) {
                if (!msalInstance.getActiveAccount()) {
                    msalInstance.setActiveAccount(accounts[0]);
                }
            }

            isInitialized = true;
        })
        .catch((error) => {
            console.error("MSAL initialization failed:", error);
            initializationPromise = null;
            isInitialized = false;
            throw error;
        });

    return initializationPromise;
};

export const ensureMsalReady = async (): Promise<void> => {
    if (!isInitialized) {
        await initializeMsal();
    }
};

const ZS_DETECT_TIMEOUT_MS = 5000;

/**
 * Probe whether the browser has an active Azure AD / Entra session (ZS system).
 *
 * Uses MSAL `ssoSilent` — a hidden-iframe flow to login.microsoftonline.com
 * that detects an existing Azure AD session cookie without requiring a prior
 * interactive login on this origin. If the user is signed into any ZS Entra
 * app in this browser (Outlook, SharePoint, Teams web, etc.), the iframe
 * resolves with account info and we route to the ZS Cognito IDP.
 *
 * Falls back to false when:
 *   - No Azure AD session cookie exists (non-ZS user)
 *   - Third-party cookies are blocked (incognito, Safari strict mode)
 *   - The probe times out (network issues)
 *   - MSAL init fails (missing config)
 *
 * Never throws — all failures resolve to false (→ client IDP).
 */
export const detectZsSession = async (): Promise<boolean> => {
    try {
        await ensureMsalReady();

        console.log("[MSAL-zs-detect] attempting ssoSilent to detect ZS Entra session");

        const ssoPromise = msalInstance.ssoSilent({
            scopes: ["openid", "profile"],
        });

        const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("ZS detection timed out")), ZS_DETECT_TIMEOUT_MS),
        );

        const response = await Promise.race([ssoPromise, timeoutPromise]);
        console.log("[MSAL-zs-detect] ssoSilent succeeded — ZS session confirmed for:", response.account?.username);
        return true;
    } catch (error) {
        console.log("[MSAL-zs-detect] ssoSilent failed — not a ZS session:", error);
        return false;
    }
};